import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Injector,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormGroup,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { ControlsOf } from '../controls-of.form-group';
import { Slot } from './slot.model';

@Component({
  selector: 'app-slot-form',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatTimepickerModule,
  ],

  templateUrl: './slot-form.html',
  styleUrl: './slot-form.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SlotForm),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SlotForm),
      multi: true,
    },
  ],
})
export class SlotForm implements OnInit, ControlValueAccessor, Validator {
  private injector = inject(Injector);

  @Input({ required: true }) formGroup!: FormGroup<ControlsOf<Slot>>;
  @Input() startLabel = 'Début';
  @Input() endLabel = 'Fin';
  @Output() valueChange = new EventEmitter<Slot>();

  public ngControl?: NgControl;

  ngOnInit(): void {
    this.setupForm();
  }

  private setupForm(): void {
    this.ngControl = this.injector.get(NgControl, null) ?? undefined;
    if (this.ngControl) {
      try {
        this.ngControl.valueAccessor = this;
      } catch {
        console.warn(
          'Impossible de définir le valueAccessor. Assurez-vous que ce composant est utilisé dans un contexte de formulaire (FormGroup, FormControl, etc.)',
        );
      }
    }
  }

  get isValid(): boolean {
    return this.formGroup.valid;
  }

  onChange: (value: Slot | undefined) => void = () => {
    // No-op
  };
  onTouched: () => void = () => {
    // No-op
  };

  writeValue(value: Slot | undefined): void {
    if (value) {
      this.formGroup?.patchValue(value);
    } else {
      this.formGroup?.reset();
    }
  }

  registerOnChange(fn: (value: Slot | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  validate(): ValidationErrors | null {
    console.warn(
      'Validation logic not implemented for SlotForm. This should be implemented based on the formGroup validity.',
    );
    return null;
  }

  get control(): AbstractControl | null {
    return this.ngControl ? (this.ngControl.control ?? null) : null;
  }
}
